/**
 * CareStart TTS preferences shared by the global Settings gear and interview pages.
 *
 * Live updates: changing the gear updates context state immediately so
 * AnonymousScmap4Wrapper / Plus HPI do not need a remount.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useAuth } from 'hooks/useAuth';
import {
  getVoicePreferences,
  resolveCareStartTtsSettings,
  saveVoicePreferences,
  setTTSProvider,
} from 'services/preferences/voicePreferencesService';
import { GEMINI_LIVE_DEFAULT_VOICE } from 'services/voice/geminiLiveVoices';
import {
  CARESTART_DEFAULT_DEEPGRAM_VOICE,
  CARESTART_DEFAULT_OPENAI_VOICE,
  CARESTART_DEFAULT_TTS_PROVIDER,
  CARESTART_TTS_PROVIDER,
} from './careStartTtsConstants';

const CareStartTtsSettingsContext = createContext(null);

const DEFAULT_SNAPSHOT = {
  ttsProvider: CARESTART_DEFAULT_TTS_PROVIDER,
  openaiVoice: CARESTART_DEFAULT_OPENAI_VOICE,
  geminiLiveVoice: GEMINI_LIVE_DEFAULT_VOICE,
  deepgramVoice: CARESTART_DEFAULT_DEEPGRAM_VOICE,
  loading: false,
  saving: false,
};

function normalizeProviderInput(next) {
  if (next === CARESTART_TTS_PROVIDER.GEMINI_FLASH_TTS) {
    return CARESTART_TTS_PROVIDER.GEMINI_FLASH_TTS;
  }
  if (next === CARESTART_TTS_PROVIDER.DEEPGRAM) {
    return CARESTART_TTS_PROVIDER.DEEPGRAM;
  }
  return CARESTART_TTS_PROVIDER.OPENAI;
}

export function CareStartTtsSettingsProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.uid || null;

  const [ttsProvider, setTtsProviderState] = useState(
    CARESTART_DEFAULT_TTS_PROVIDER,
  );
  const [openaiVoice, setOpenaiVoiceState] = useState(
    CARESTART_DEFAULT_OPENAI_VOICE,
  );
  const [geminiLiveVoice, setGeminiLiveVoiceState] = useState(
    GEMINI_LIVE_DEFAULT_VOICE,
  );
  const [deepgramVoice, setDeepgramVoiceState] = useState(
    CARESTART_DEFAULT_DEEPGRAM_VOICE,
  );
  const [loading, setLoading] = useState(Boolean(userId));
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) {
      setTtsProviderState(CARESTART_DEFAULT_TTS_PROVIDER);
      setOpenaiVoiceState(CARESTART_DEFAULT_OPENAI_VOICE);
      setGeminiLiveVoiceState(GEMINI_LIVE_DEFAULT_VOICE);
      setDeepgramVoiceState(CARESTART_DEFAULT_DEEPGRAM_VOICE);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const prefs = await getVoicePreferences(userId);
      const resolved = resolveCareStartTtsSettings(prefs);
      setTtsProviderState(resolved.ttsProvider);
      setOpenaiVoiceState(resolved.openaiVoice);
      setGeminiLiveVoiceState(resolved.geminiLiveVoice);
      setDeepgramVoiceState(resolved.deepgramVoice);
    } catch (err) {
      console.warn('[CareStartTtsSettings] load failed', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setTtsProvider = useCallback(
    async (next) => {
      const provider = normalizeProviderInput(next);
      const previous = ttsProvider;
      setTtsProviderState(provider);
      if (!userId) return;
      setSaving(true);
      try {
        const ok = await setTTSProvider(userId, provider);
        if (!ok) setTtsProviderState(previous);
      } catch (err) {
        console.warn('[CareStartTtsSettings] setTtsProvider failed', err);
        setTtsProviderState(previous);
      } finally {
        setSaving(false);
      }
    },
    [ttsProvider, userId],
  );

  const setOpenaiVoice = useCallback(
    async (voiceId) => {
      const voice =
        typeof voiceId === 'string' && voiceId.trim()
          ? voiceId.trim()
          : CARESTART_DEFAULT_OPENAI_VOICE;
      const previous = openaiVoice;
      setOpenaiVoiceState(voice);
      if (!userId) return;
      setSaving(true);
      try {
        const ok = await saveVoicePreferences(userId, {
          openai: { voice },
        });
        if (!ok) setOpenaiVoiceState(previous);
      } catch (err) {
        console.warn('[CareStartTtsSettings] setOpenaiVoice failed', err);
        setOpenaiVoiceState(previous);
      } finally {
        setSaving(false);
      }
    },
    [openaiVoice, userId],
  );

  const setGeminiLiveVoice = useCallback(
    async (voiceId) => {
      const voice =
        typeof voiceId === 'string' && voiceId.trim()
          ? voiceId.trim()
          : GEMINI_LIVE_DEFAULT_VOICE;
      const previous = geminiLiveVoice;
      setGeminiLiveVoiceState(voice);
      if (!userId) return;
      setSaving(true);
      try {
        const ok = await saveVoicePreferences(userId, {
          geminiLive: { voice },
        });
        if (!ok) setGeminiLiveVoiceState(previous);
      } catch (err) {
        console.warn('[CareStartTtsSettings] setGeminiLiveVoice failed', err);
        setGeminiLiveVoiceState(previous);
      } finally {
        setSaving(false);
      }
    },
    [geminiLiveVoice, userId],
  );

  const setDeepgramVoice = useCallback(
    async (voiceId) => {
      const voice =
        typeof voiceId === 'string' && voiceId.trim()
          ? voiceId.trim()
          : CARESTART_DEFAULT_DEEPGRAM_VOICE;
      const previous = deepgramVoice;
      setDeepgramVoiceState(voice);
      if (!userId) return;
      setSaving(true);
      try {
        const ok = await saveVoicePreferences(userId, {
          deepgramSettings: { ttsVoice: voice },
        });
        if (!ok) setDeepgramVoiceState(previous);
      } catch (err) {
        console.warn('[CareStartTtsSettings] setDeepgramVoice failed', err);
        setDeepgramVoiceState(previous);
      } finally {
        setSaving(false);
      }
    },
    [deepgramVoice, userId],
  );

  /** Voice id for classic / Flash / Aura speak path based on current provider. */
  const ttsVoice =
    ttsProvider === CARESTART_TTS_PROVIDER.GEMINI_FLASH_TTS
      ? geminiLiveVoice
      : ttsProvider === CARESTART_TTS_PROVIDER.DEEPGRAM
        ? deepgramVoice
        : openaiVoice;

  const value = useMemo(
    () => ({
      ttsProvider,
      openaiVoice,
      geminiLiveVoice,
      deepgramVoice,
      ttsVoice,
      loading,
      saving,
      setTtsProvider,
      setOpenaiVoice,
      setGeminiLiveVoice,
      setDeepgramVoice,
      refresh,
    }),
    [
      ttsProvider,
      openaiVoice,
      geminiLiveVoice,
      deepgramVoice,
      ttsVoice,
      loading,
      saving,
      setTtsProvider,
      setOpenaiVoice,
      setGeminiLiveVoice,
      setDeepgramVoice,
      refresh,
    ],
  );

  return (
    <CareStartTtsSettingsContext.Provider value={value}>
      {children}
    </CareStartTtsSettingsContext.Provider>
  );
}

CareStartTtsSettingsProvider.propTypes = {
  children: PropTypes.node,
};

/** Safe for chrome that may render outside the provider during tests. */
export function useCareStartTtsSettingsOptional() {
  return useContext(CareStartTtsSettingsContext);
}

export function useCareStartTtsSettings() {
  const ctx = useContext(CareStartTtsSettingsContext);
  if (!ctx) {
    return {
      ...DEFAULT_SNAPSHOT,
      ttsVoice: CARESTART_DEFAULT_OPENAI_VOICE,
      setTtsProvider: async () => {},
      setOpenaiVoice: async () => {},
      setGeminiLiveVoice: async () => {},
      setDeepgramVoice: async () => {},
      refresh: async () => {},
    };
  }
  return ctx;
}
